import merge from "ts-deepmerge";

import {
  EmployerPermissions,
  EngagePermissions as NativeEngagePermissions,
  TrackyrPermissions,
} from "./queries";
import type {
  Access,
  ShortlysterPermissions as ShortlysterPermissions,
} from "./types";
import { GroupRole } from "~/lib/federationGateway/graphql";
import type {
  FetchDevelopPermissionsQuery,
  MyGroupMembersQuery,
} from "~/lib/federationGateway/platformGraphql";

export type EngagePermissions = {
  insights?: {
    view?: boolean;
    edit?: boolean;
  };
  campaigns?: {
    view?: boolean;
    edit?: boolean;
  };
  shortlyster?: {
    users?: boolean;
    account?: boolean;
    settings?: boolean;
  };
};

type Simplify<T> = {
  [K in keyof T]: Simplify<T[K]> extends infer O
    ? { [K in keyof O]: O[K] }
    : never;
};

export type DevelopPermissions = {
  learningPortal: boolean;
};

export type Permissions = Simplify<
  ShortlysterPermissions &
    TrackyrPermissions &
    EmployerPermissions &
    EngagePermissions & {
      develop?: DevelopPermissions;
      product?: {
        engage?: boolean;
        engageAdmin?: boolean;
        hire?: boolean;
        develop?: boolean;
        developAdmin?: boolean;
        insights?: boolean;
      };
      compono?: {
        admin?: boolean;
      };
    }
>;

const transformEngagePermissions = (perms?: NativeEngagePermissions) => {
  if (!perms) return {};

  const engagePerms: EngagePermissions = {};

  perms.permissions.forEach((p) => {
    const [feat, perm] = p.split(".");
    if (!engagePerms[feat as keyof EngagePermissions]) {
      engagePerms[feat as keyof EngagePermissions] = {};
    }
    (engagePerms[feat as keyof EngagePermissions] as any)[perm] = true;
  });
  return engagePerms;
};

export const resolvePermissions = (
  roles: Access["allAppsRoles"],
  sl: ShortlysterPermissions,
  tr: TrackyrPermissions,
  em: EmployerPermissions,
  en?: NativeEngagePermissions,
): Permissions => {
  // Job Requisition capabilities are role-derived (the permissions query does
  // not return them). Mirrors the listings subgraph's capability matrix
  // (services/listings requisitionPermissions.ts): configure is Owner/Admin
  // only; the tab (TP-1598) gates on `jobRequisitions.configure`.
  const hireRoles =
    roles?.find((r) => r.appCode === "shortlyster")?.roles ?? [];
  const hasHireRole = (...allowed: string[]) =>
    hireRoles.some((role) => allowed.includes(role));
  // Spec §9 matrix. Requisition-page access (view/manage) covers
  // Owner/Admin/Recruitment Manager (`user`; `editor` is also granted it, but
  // maps to no product role — see requisitionPermissions.ts) and Hiring Managers
  // (hiringManager) — team scoping ("same team") is enforced server-side, so
  // these flags only gate navigation/routes. Creating a Job from an approved
  // requisition is NOT covered here — it is a server-resolved per-record
  // permission (`createJob`), which excludes Hiring Managers. Reviewers have
  // none. Any Hire role can be a named
  // approver, so the Approvals inbox (`viewApprovals`) is open to anyone with
  // Hire access — expressed as "holds any Hire role" rather than an explicit
  // list, so a role added later doesn't silently lose access to its own inbox.
  // (The nav additionally requires `ApprovalConfig.enabled` — see HireNav.)
  // `viewAllApprovals` remains Owner/Admin (§5.3).
  const jobRequisitions = {
    view: hasHireRole("owner", "admin", "editor", "user", "hiringManager"),
    create: hasHireRole("owner", "admin", "editor", "user", "hiringManager"),
    manage: hasHireRole("owner", "admin", "editor", "user", "hiringManager"),
    configure: hasHireRole("owner", "admin"),
    viewApprovals: hireRoles.length > 0,
    viewAllApprovals: hasHireRole("owner", "admin"),
  };

  // Offer Management capabilities, mirroring the applications subgraph's
  // matrix (offerPermissions.ts). Role-only: the hiring-team scope ("if in the
  // hiring team") is enforced server-side, so these flags only gate
  // navigation and controls. The approver action is per-record (named
  // approver), not covered here. Hiring Managers view the Offer tab read-only.
  const offers = {
    view: hasHireRole("owner", "admin", "editor", "user", "hiringManager"),
    manage: hasHireRole("owner", "admin", "editor", "user"),
    configureApprovals: hasHireRole("owner", "admin"),
    configureTemplates: hasHireRole("owner", "admin", "editor", "user"),
    viewApprovals: hasHireRole(
      "owner",
      "admin",
      "editor",
      "user",
      "hiringManager",
      "viewer",
    ),
  };

  // The organisation's own third-party provider connections (DocuSign first):
  // connecting and disconnecting one is owner/admin, like the offer settings.
  const integrations = {
    configure: hasHireRole("owner", "admin"),
  };

  return merge(
    sl,
    tr ?? {},
    em,
    transformEngagePermissions(en),
    { jobRequisitions },
    { offers },
    { integrations },
    {
      product: {
        // the following /should/ work, but doesn't if the user has only engage access
        // engage: Boolean(roles?.find((r) => r.appCode === "retayner")),
        engage: !!en,
        engageAdmin: Boolean(
          roles?.find(
            (r) =>
              r.appCode === "retayner" &&
              r.roles?.some((role) => role === "owner" || role === "admin"),
          ),
        ),
        hire: Boolean(roles?.find((r) => r.appCode === "shortlyster")),
        develop: Boolean(roles?.find((r) => r.appCode === "develop")),
        developAdmin: Boolean(
          roles?.find(
            (r) =>
              r.appCode === "develop" &&
              r.roles?.some((role) => role === "owner" || role === "admin"),
          ),
        ),
      },
    },
    { compono: { admin: !!sl.isComponoAdmin } },
  );
};

/**
 * The Develop portal opens on either of two grants: the develop subgraph's own
 * `learningPortal` flag, or holding a group membership above learner.
 *
 * Both reads may be absent, and absent means "not granted" rather than "not yet
 * known" — `Query.permissions` errors for a caller with no Develop access, and
 * `myGroupMembers` is nullable, so neither can be assumed present by the time
 * the provider resolves.
 */
export const resolveDevelopPermissions = (
  developData?: FetchDevelopPermissionsQuery,
  groupsData?: MyGroupMembersQuery,
): DevelopPermissions => ({
  learningPortal:
    Boolean(developData?.permissions?.learningPortal) ||
    hasMembershipAboveLearner(groupsData),
});

/**
 * An unset role is a learner, not an unknown: organisations reports one as
 * `roleDisplayName: "Learner"`, so granting the portal on it would open a door
 * the rest of the product says is shut.
 */
const hasMembershipAboveLearner = (groupsData?: MyGroupMembersQuery) =>
  (groupsData?.myGroupMembers ?? []).some(
    (member) => member?.role != null && member.role !== GroupRole.Learner,
  );
